import { useEffect, useRef, useState } from "react";
import { Send, Bot, Sparkles } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { API } from "@/lib/api";
import { textareaCls } from "@/lib/constants";

const SUGGESTIONS = [
  "What is currently live on the deal board?",
  "Which deals fit a real estate and real assets mandate?",
  "How do I request an introduction?",
];

let _msgSeq = 0;
const makeMsgId = () => {
  _msgSeq += 1;
  return `m${Date.now()}-${_msgSeq}`;
};

export default function OrlaChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: makeMsgId(), role: "user", content: msg }, { id: makeMsgId(), role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/orla/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, message: msg }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.type === "meta") setConvId(data.conversation_id);
          else if (data.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: copy[copy.length - 1].content + data.content };
              return copy;
            });
          } else if (data.type === "error") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "ORLA is unavailable momentarily. Please try again." };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "Connection interrupted. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <PortalLayout title="ORLA">
      <div className="max-w-3xl mx-auto">
        <GlassCard className="flex flex-col h-[70vh]" data-testid="orla-chat-surface">
          <div className="flex items-center gap-3 p-5 border-b border-border">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--pdg-gold)/0.14)] flex items-center justify-center">
              <Bot size={18} className="text-[hsl(var(--pdg-gold))]" />
            </div>
            <div>
              <div className="text-sm text-foreground">ORLA</div>
              <div className="text-[11px] text-muted-foreground">Intelligence layer &middot; discreet by design</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Sparkles className="text-[hsl(var(--pdg-gold))]" size={22} />
                <p className="mt-4 text-sm text-muted-foreground max-w-sm">Ask ORLA about the pipeline, your mandate fit, or how to request an introduction.</p>
                <div className="mt-6 flex flex-col gap-2 w-full max-w-sm">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-left text-sm px-4 h-10 rounded-lg border border-border text-foreground/80 hover:border-[hsl(var(--pdg-gold)/0.4)] transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id} data-testid="orla-chat-message" className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-7 ${m.role === "user"
                  ? "bg-[hsl(var(--pdg-elevated)/0.7)] border border-border text-foreground"
                  : "pdg-glass text-foreground/90"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="opacity-60">ORLA is thinking…</span> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-border">
            <div className="flex gap-3 items-end">
              <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} data-testid="orla-chat-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Message ORLA…" className={textareaCls + " resize-none max-h-32"} />
              <button onClick={() => send()} disabled={streaming} data-testid="orla-chat-send-button"
                className="shrink-0 w-11 h-11 rounded-xl border border-[hsl(var(--pdg-gold)/0.5)] text-[hsl(var(--pdg-gold))] hover:bg-[hsl(var(--pdg-gold)/0.12)] transition-colors flex items-center justify-center disabled:opacity-50">
                <Send size={17} />
              </button>
            </div>
          </div>
        </GlassCard>
      </div>
    </PortalLayout>
  );
}
